/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import BioIndexQuizNavigation from "../components/BioIndexQuizNavigation";
import "../styles/BioIndexCommunityMobile.css";
import "../styles/BioIndexQuiz.css";
import {
  generaByFamily,
  familiesByOrder,
  ordersByClass,
  classesByPhylum,
  phylaByKingdom,
} from "../utils/taxonomy";

function uniqueSorted(arr) {
  return [...new Set(arr.filter(Boolean))].sort((a, b) =>
    String(a).localeCompare(String(b)),
  );
}

const quizTypes = [
  {
    id: "scientific-from-photo",
    label: "Scientific name",
    description: "See a photograph and identify its scientific name.",
    marker: "Aa",
  },
  {
    id: "common-from-photo",
    label: "Common name",
    description: "See a photograph and identify its common name.",
    marker: "A",
  },
  {
    id: "genus-from-photo",
    label: "Genus",
    description: "Place the photographed organism in the correct genus.",
    marker: "G",
  },
  {
    id: "family-from-photo",
    label: "Family",
    description: "Place the photographed organism in the correct family.",
    marker: "F",
  },
  {
    id: "photo-from-scientific",
    label: "Photo from scientific name",
    description: "Match a scientific name to one of four photographs.",
    marker: "4×",
  },
  {
    id: "photo-from-common",
    label: "Photo from common name",
    description: "Match a common name to one of four photographs.",
    marker: "4×",
  },
];

const taxonLabels = {
  kingdom: "Kingdom",
  phylum: "Phylum",
  class: "Class",
  order: "Order",
  family: "Family",
  genus: "Genus",
};

function resolveTaxonomy(genus) {
  const g = String(genus || "")
    .toLowerCase()
    .trim();

  const family = Object.keys(generaByFamily).find((f) =>
    (generaByFamily[f] || []).some(
      (x) => String(x).toLowerCase().trim() === g,
    ),
  );

  const order = Object.keys(familiesByOrder).find((o) =>
    (familiesByOrder[o] || []).some(
      (f) =>
        String(f).toLowerCase().trim() ===
        String(family || "")
          .toLowerCase()
          .trim(),
    ),
  );

  const className = Object.keys(ordersByClass).find((c) =>
    (ordersByClass[c] || []).some(
      (o) =>
        String(o).toLowerCase().trim() ===
        String(order || "")
          .toLowerCase()
          .trim(),
    ),
  );

  const phylum = Object.keys(classesByPhylum).find((p) =>
    (classesByPhylum[p] || []).some(
      (c) =>
        String(c).toLowerCase().trim() ===
        String(className || "")
          .toLowerCase()
          .trim(),
    ),
  );

  const kingdom = Object.keys(phylaByKingdom).find((k) =>
    (phylaByKingdom[k] || []).some(
      (p) =>
        String(p).toLowerCase().trim() ===
        String(phylum || "")
          .toLowerCase()
          .trim(),
    ),
  );

  return { genus: g, family, order, className, phylum, kingdom };
}

export default function QuizSetupPage() {
  const navigate = useNavigate();

  const [quizType, setQuizType] = useState("scientific-from-photo");
  const [questionCount, setQuestionCount] = useState(10);

  const [taxonLevel, setTaxonLevel] = useState("");
  const [taxonValue, setTaxonValue] = useState("");

  const [species, setSpecies] = useState([]);

  useEffect(() => {
    let cancelled = false;

    async function loadSpecies() {
      try {
        const uid = auth.currentUser?.uid;
        if (!uid) return;

        const snap = await getDocs(collection(db, "users", uid, "species"));
        const list = [];

        snap.forEach((docSnap) => {
          const data = docSnap.data();
          if (!data.forms || data.forms.length === 0) return;
          list.push({ name: data.name, genus: data.genus });
        });

        if (!cancelled) setSpecies(list);
      } catch (error) {
        console.error("Unable to load quiz species:", error);
      }
    }

    loadSpecies();
    return () => {
      cancelled = true;
    };
  }, []);

  const options = useMemo(() => {
    if (taxonLevel === "kingdom") {
      return uniqueSorted(Object.keys(phylaByKingdom));
    }

    if (taxonLevel === "phylum") {
      return uniqueSorted(Object.values(phylaByKingdom).flat());
    }

    if (taxonLevel === "class") {
      return uniqueSorted(Object.values(classesByPhylum).flat());
    }

    if (taxonLevel === "order") {
      return uniqueSorted(Object.keys(familiesByOrder));
    }

    if (taxonLevel === "family") {
      return uniqueSorted(Object.keys(generaByFamily));
    }

    if (taxonLevel === "genus") {
      return uniqueSorted(Object.values(generaByFamily).flat());
    }

    return [];
  }, [taxonLevel]);

  const taxonCounts = useMemo(() => {
    const counts = {
      genus: {},
      family: {},
      order: {},
      class: {},
      phylum: {},
      kingdom: {},
    };

    species.forEach((sp) => {
      const t = resolveTaxonomy(sp.genus);
      if (!t) return;

      counts.genus[sp.genus] = (counts.genus[sp.genus] || 0) + 1;
      if (t.family)
        counts.family[t.family] = (counts.family[t.family] || 0) + 1;
      if (t.order) counts.order[t.order] = (counts.order[t.order] || 0) + 1;
      if (t.className)
        counts.class[t.className] = (counts.class[t.className] || 0) + 1;
      if (t.phylum)
        counts.phylum[t.phylum] = (counts.phylum[t.phylum] || 0) + 1;
      if (t.kingdom)
        counts.kingdom[t.kingdom] = (counts.kingdom[t.kingdom] || 0) + 1;
    });

    return counts;
  }, [species]);

  const filteredOptions = useMemo(() => {
    if (!taxonLevel) return options;

    return options.filter((opt) => {
      const key = String(opt || "")
        .toLowerCase()
        .trim();
      const bucket = taxonCounts[taxonLevel] || {};
      const count = bucket[key] || bucket[opt] || 0;
      return count >= 4;
    });
  }, [options, taxonLevel, taxonCounts]);

  const availableSpecies = useMemo(() => {
    if (!taxonLevel || !taxonValue) return species;

    const target = String(taxonValue || "")
      .toLowerCase()
      .trim();

    return species.filter((sp) => {
      const t = resolveTaxonomy(sp.genus);

      if (taxonLevel === "genus")
        return (
          String(t.genus || "")
            .toLowerCase()
            .trim() === target
        );

      if (taxonLevel === "family")
        return (
          String(t.family || "")
            .toLowerCase()
            .trim() === target
        );

      if (taxonLevel === "order")
        return (
          String(t.order || "")
            .toLowerCase()
            .trim() === target
        );

      if (taxonLevel === "class")
        return (
          String(t.className || "")
            .toLowerCase()
            .trim() === target
        );

      if (taxonLevel === "phylum")
        return (
          String(t.phylum || "")
            .toLowerCase()
            .trim() === target
        );

      if (taxonLevel === "kingdom")
        return (
          String(t.kingdom || "")
            .toLowerCase()
            .trim() === target
        );

      return true;
    });
  }, [species, taxonLevel, taxonValue]);

  const maxQuestions = availableSpecies.length;

  function startQuiz() {
    if (taxonLevel && !taxonValue) return;

    const filters = taxonLevel ? { [taxonLevel]: taxonValue } : null;

    navigate("/quiz/play", {
      state: {
        quizType,
        filters,
        questionCount: Math.min(questionCount, maxQuestions),
      },
    });
  }
  const selectedQuiz =
    quizTypes.find((type) => type.id === quizType) || quizTypes[0];
  const selectionRequired = Boolean(taxonLevel && !taxonValue);

  return (
    <div className="bioindex-quiz-page bioindex-quiz-setup">
      <BioIndexQuizNavigation />
      <main className="bioindex-quiz-container">
        <header className="bioindex-quiz-intro-card">
          <div className="bioindex-quiz-intro-icon" aria-hidden="true">?</div>
          <div>
            <p className="bioindex-quiz-overline">BioIndex · Taxonomy Challenge</p>
            <h1>Build Your Quiz</h1>
            <p className="bioindex-quiz-subtitle">Choose a question type, a collection focus, and the number of questions.</p>
          </div>
          <div className="bioindex-quiz-library-total" aria-label={`${species.length} species available`}>
            <strong>{species.length.toLocaleString()}</strong>
            <span>species available</span>
          </div>
        </header>

        <div className="bioindex-quiz-setup-grid">
          <section className="bioindex-quiz-card bioindex-quiz-types-card" aria-labelledby="quiz-question-types">
            <div className="bioindex-quiz-section-heading">
              <h2 id="quiz-question-types">Choose the challenge</h2>
              <span>6 question types</span>
            </div>
            <div className="bioindex-quiz-type-grid">
              {quizTypes.map((type) => {
                const isSelected = quizType === type.id;
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setQuizType(type.id)}
                    aria-pressed={isSelected}
                    className={`bioindex-quiz-type-option${isSelected ? " is-selected" : ""}`}
                  >
                    <span className="bioindex-quiz-type-marker" aria-hidden="true">{type.marker}</span>
                    <span className="bioindex-quiz-type-copy">
                      <strong>{type.label}</strong>
                      <small>{type.description}</small>
                    </span>
                    <span className="bioindex-quiz-selected-indicator" aria-hidden="true">{isSelected ? "✓" : ""}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <aside className="bioindex-quiz-card bioindex-quiz-setup-controls" aria-labelledby="quiz-round-settings">
            <div className="bioindex-quiz-section-heading">
              <h2 id="quiz-round-settings">Your round</h2>
              <span>Customize</span>
            </div>
            <div className="bioindex-quiz-field">
              <label htmlFor="quiz-taxon-level">Focus</label>
              <select
                id="quiz-taxon-level"
                value={taxonLevel}
                onChange={(event) => {
                  setTaxonLevel(event.target.value);
                  setTaxonValue("");
                }}
              >
                <option value="">My entire collection</option>
                <option value="kingdom">Kingdom</option>
                <option value="phylum">Phylum</option>
                <option value="class">Class</option>
                <option value="order">Order</option>
                <option value="family">Family</option>
                <option value="genus">Genus</option>
              </select>
            </div>
            {taxonLevel && (
              <div className="bioindex-quiz-field">
                <label htmlFor="quiz-taxon-value">Choose {taxonLabels[taxonLevel].toLowerCase()}</label>
                <select
                  id="quiz-taxon-value"
                  value={taxonValue}
                  onChange={(event) => setTaxonValue(event.target.value)}
                >
                  <option value="">Select {taxonLabels[taxonLevel].toLowerCase()}...</option>
                  {filteredOptions.map((option) => (
                    <option key={option} value={option}>
                      {String(option).charAt(0).toUpperCase() + String(option).slice(1).toLowerCase()}
                    </option>
                  ))}
                </select>
                {filteredOptions.length === 0 && <p className="bioindex-quiz-field-note">No taxonomic groups have enough species for this selection.</p>}
              </div>
            )}
            <div className="bioindex-quiz-field">
              <div className="bioindex-quiz-label-row">
                <label htmlFor="quiz-question-count">Questions</label>
                <span>{maxQuestions.toLocaleString()} available</span>
              </div>
              <div className="bioindex-quiz-number-picker">
                <button
                  type="button"
                  onClick={() => setQuestionCount((count) => Math.max(1, count - 1))}
                  aria-label="Remove one question"
                  disabled={maxQuestions === 0}
                >−</button>
                <input
                  id="quiz-question-count"
                  type="number"
                  min="1"
                  max={maxQuestions || 1}
                  value={Math.min(questionCount, maxQuestions || 1)}
                  onChange={(event) => setQuestionCount(Math.min(maxQuestions || 1, Math.max(1, Number(event.target.value) || 1)))}
                />
                <button
                  type="button"
                  onClick={() => setQuestionCount((count) => Math.min(maxQuestions || 1, count + 1))}
                  aria-label="Add one question"
                  disabled={maxQuestions === 0}
                >+</button>
              </div>
            </div>
            <div className="bioindex-quiz-round-summary">
              <span>Selected challenge</span>
              <strong>{selectedQuiz.label}</strong>
              <small>
                {taxonLevel
                  ? taxonValue
                    ? `${taxonLabels[taxonLevel]}: ${taxonValue}`
                    : `Choose a ${taxonLabels[taxonLevel].toLowerCase()} to continue.`
                  : "All species in your collection"}
              </small>
            </div>
            <button
              type="button"
              onClick={startQuiz}
              disabled={maxQuestions === 0 || selectionRequired}
              className="bioindex-quiz-primary bioindex-quiz-start"
            >
              Start Quiz <span aria-hidden="true">→</span>
            </button>
            {maxQuestions === 0 && <p className="bioindex-quiz-field-note">Upload observations to make them available for your quiz.</p>}
          </aside>
        </div>
      </main>
    </div>
  );
}
